import { useState } from 'react';
import { READ_CONFIDENCE_FLOOR, type StepMarkView, type StepVerdict } from '@padcanvas/shared';
import { LatexText } from '../latex';
import './StepsAnalysis.css';

export interface StepItem {
  stepNumber: number;
  content: string;
  feedback?: string;
  isCorrect: boolean;
  /** Tri-state step-correctness (tutor-feedback F4). `locallyValid` = was the
   *  step a sound move given the student's OWN prior step; `onCorrectPath` =
   *  does it lead toward a correct solution. Present on the post-attempt /
   *  gated path; absent on legacy data (falls back to `isCorrect`). */
  locallyValid?: StepVerdict;
  onCorrectPath?: StepVerdict;
  /** The student struck this line through — shown, never judged. */
  crossedOut?: boolean;
  /** 0–1: how sure the analyser was of what this line says. */
  readConfidence?: number;
  /** A teacher's mark — outranks the analyser's verdict. */
  marking?: StepMarkView;
}

/** A teacher marks a step: `verdict` and, when it disagrees with the
 *  analyser, why we got it wrong. `step` is the 1-based position in `steps`. */
export type MarkStepHandler = (
  step: number, verdict: 'right' | 'wrong' | 'clear', why?: 'misread' | 'misjudged', text?: string,
) => Promise<unknown>;

export interface StepsAnalysisProps {
  steps: StepItem[];
  title?: string;
  /** F4 — when true (behind `analysis_view_v2`), render the 3-state view:
   *  a step that's sound-but-off-track (locally valid, off the correct path)
   *  shows as "off-track" (amber) rather than a flat red, and the first
   *  off-path step is anchored as "first issue". Off = the legacy binary
   *  correct/incorrect. Defaults off so non-v2 and other consumers are
   *  unchanged. See docs/FeatureDesigns/diagnosis/tutor-feedback-model.md. */
  richSteps?: boolean;
  /** Suppress the per-step commentary on steps that went RIGHT, rendering them
   *  as a single compact line (verdict rail + the step itself). Across a 600-
   *  analysis sample, 57% of step-feedback words were spent narrating correct
   *  steps — text that tells the student what they already know they did. Off
   *  by default so admin/teacher review still sees the model's full output. */
  collapseCorrect?: boolean;
  /** The analysis was low-trust (`feedback.trust.level === 'low'`): it may
   *  rest on a misreading, so no step is anchored as "first issue". */
  lowTrust?: boolean;
  /** Render the section as a disclosure under its title, closed until opened.
   *  The step list is the longest part of a full assessment and the part read
   *  least often; the scores, verdict and guidance above it are what a reader
   *  came for. Needs a title — the title is what is clicked. */
  collapsible?: boolean;
  /** The teacher's ✓ / ✗ on each step. Omitted ⇒ no control. Needs `steps` in
   *  the analysis's own order — the position is the step's identity. */
  onMarkStep?: MarkStepHandler;
  /** The tag on a marked step. Default: the student's wording. */
  markLabel?: (m: StepMarkView) => string;
}

type StepState = 'correct' | 'offpath' | 'incorrect' | 'unsure' | 'crossed';

/** What a neutral step says about itself — never a verdict. */
const NEUTRAL_TAG: Partial<Record<StepState, string>> = {
  crossed: 'Crossed out — not marked',
  unsure: 'Couldn’t read this clearly — not marked',
};

/**
 * Resolve a step's display state. In binary mode (or when the tri-state is
 * absent) it's just correct/incorrect from `isCorrect`. In rich mode it splits
 * the "wrong" bucket: a step that left the correct path but was itself sound
 * ("correct execution of a flawed plan") is `offpath`, not `incorrect`.
 */
export function stepState(s: StepItem, rich: boolean): StepState {
  // The student's own strike-through outranks any verdict, in either mode.
  if (s.crossedOut) return 'crossed';
  // A teacher's mark outranks the analyser's (step-marking-target.md).
  if (s.marking) return s.marking.verdict === 'right' ? 'correct' : 'incorrect';
  return analyserState(s, rich);
}

/** What the analyser said of the step, before any mark. */
export function analyserState(s: StepItem, rich: boolean): StepState {
  if (s.crossedOut) return 'crossed';
  if (!rich || (s.onCorrectPath === undefined && s.locallyValid === undefined)) {
    return s.isCorrect ? 'correct' : 'incorrect';
  }
  const onPath = s.onCorrectPath ?? (s.isCorrect ? 'yes' : 'no');
  if (onPath === 'yes') return 'correct';
  if (onPath === 'unsure') return 'unsure';
  // Off the path, but on a line the analyser could not read, or read with
  // doubt: "we couldn't tell" is not "you were wrong". Showing it red is how a
  // correct script came to look like two mistakes (prod 2HdLHYKoTWj8HVDK).
  if (s.locallyValid === 'unsure') return 'unsure';
  if (typeof s.readConfidence === 'number' && s.readConfidence < READ_CONFIDENCE_FLOOR) return 'unsure';
  // Off the correct path: a locally-valid step is "off-track but sound"; only a
  // locally-invalid step is a genuine error.
  return s.locallyValid === 'yes' ? 'offpath' : 'incorrect';
}

/**
 * The analysis LLM frequently emits a step's `content` as a pure LaTeX
 * expression with no `$...$` delimiters (e.g. `x = -5 \pm \frac{\sqrt{25}}{4}`).
 * `<LatexText>` only renders math when delimiters are present, so without
 * this wrapper the raw LaTeX shows up as plain text.
 *
 * Heuristic: if the text contains a backslash command (`\frac`, `\sqrt`,
 * `\pm`, …) AND has no math delimiters, wrap the entire text as inline
 * math. Plain prose passes through untouched. We only apply this on
 * `step.content` (typically a single mathematical line); `step.feedback`
 * is mixed prose and stays as-is so its incidental backslash commands
 * don't accidentally swallow surrounding text into math mode.
 */
export function autoWrapLatex(text: string): string {
  if (!text) return text;
  // Detect existing delimiters: `$` (not preceded by `\`), `\[`, or `\(`.
  const hasDelimiters = /(?:^|[^\\])\$|\\\[|\\\(/.test(text);
  if (hasDelimiters) return text;
  if (/\\[a-zA-Z]+/.test(text)) return `$${text}$`;
  return text;
}

const STUDENT_MARK_LABEL = () => 'Checked by your teacher';

export function StepsAnalysis({ steps, title = 'Step analysis', richSteps = false, collapseCorrect = false, lowTrust = false, collapsible = false, onMarkStep, markLabel = STUDENT_MARK_LABEL }: StepsAnalysisProps) {
  // First-break anchor: the first step (in order) that leaves the correct path.
  // None on a low-trust analysis — the break it would point at may be ours —
  // unless a teacher marked it: then it is not ours.
  const ordered = [...steps].sort((a, b) => a.stepNumber - b.stepNumber);
  const firstBreak = richSteps
    ? ordered.find(s => { const st = stepState(s, true); return st === 'offpath' || st === 'incorrect'; })
    : undefined;
  const firstBreakNumber = firstBreak && (!lowTrust || firstBreak.marking) ? firstBreak.stepNumber : undefined;

  const list = steps.length === 0 ? (
    <p className="sa-no-working">No working provided.</p>
  ) : (
    <div className="sa-steps-list">
      {steps.map((step, i) => {
        const state = stepState(step, richSteps);
        const isFirstBreak = richSteps && step.stepNumber === firstBreakNumber;
        const muted = collapseCorrect && state === 'correct';
        // A mark against the analyser, or a misread line, makes its comment on
        // the step wrong: the student does not see it; the teacher sees it as
        // what was overruled. The line the teacher says is written replaces
        // the misreading (step-marking-target.md).
        const overruled = isOverruled(step);
        const reads = step.marking?.text;
        return (
          <div key={step.stepNumber} data-step={step.stepNumber} className={`sa-step-item ${state}${isFirstBreak ? ' first-break' : ''}${muted ? ' muted' : ''}`}>
            <div className="sa-step-rail">Step {step.stepNumber}</div>
            <div className="sa-step-body">
              {isFirstBreak && <div className="sa-first-break-tag">First issue here</div>}
              {step.marking && <div className="sa-marked-tag">{markLabel(step.marking)}</div>}
              {!step.marking && NEUTRAL_TAG[state] && <div className="sa-neutral-tag">{NEUTRAL_TAG[state]}</div>}
              <div className="sa-step-content"><LatexText text={autoWrapLatex(reads ?? step.content)} /></div>
              {reads && onMarkStep && (
                <div className="sa-step-feedback overruled">
                  <span className="sa-overruled-lab">We read it as: </span><LatexText text={autoWrapLatex(step.content)} />
                </div>
              )}
              {step.feedback && (overruled ? !!onMarkStep : !muted) && (
                <div className={`sa-step-feedback${overruled ? ' overruled' : ''}`}>
                  {overruled && <span className="sa-overruled-lab">The analysis said: </span>}
                  <LatexText text={step.feedback} />
                </div>
              )}
              {onMarkStep && !step.crossedOut && (
                <StepMarkControl step={i + 1} item={step} system={verdictOf(analyserState(step, richSteps))} onMark={onMarkStep} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (collapsible && title) {
    return (
      <details className="sa-steps-analysis sa-steps-collapsible">
        <summary className="sol-section-title sa-steps-summary">
          {title}
          {steps.length > 0 && <span className="sa-steps-count"> · {steps.length} {steps.length === 1 ? 'step' : 'steps'}</span>}
        </summary>
        {list}
      </details>
    );
  }

  return (
    <div className="sa-steps-analysis">
      {title && <h3 className="sol-section-title">{title}</h3>}
      {list}
    </div>
  );
}

/** A display state as a verdict — off the path is wrong. */
export const verdictOf = (st: StepState): 'right' | 'wrong' | 'unsure' | undefined =>
  st === 'correct' ? 'right' : st === 'incorrect' || st === 'offpath' ? 'wrong' : st === 'unsure' ? 'unsure' : undefined;

/** The analyser's comment on a marked step no longer stands. The server says
 *  so (`commentOverruled`); a page without it compares verdicts. */
export function isOverruled(s: StepItem): boolean {
  const m = s.marking;
  if (!m) return false;
  if (m.commentOverruled !== undefined) return m.commentOverruled;
  return m.why === 'misread' || verdictOf(analyserState(s, true)) !== m.verdict;
}

/**
 * The teacher's correction of one step. Two separate things a teacher can say:
 * whether the STUDENT's step is right or wrong — one tap — and that we misread
 * the line, whatever its verdict (prod p9P7S3KoOhX8SpqS: misread, still right).
 * A verdict against the analyser asks one optional question: why we got it wrong.
 */
function StepMarkControl({ step, item, system, onMark }: {
  step: number;
  item: StepItem;
  system: 'right' | 'wrong' | 'unsure' | undefined;
  onMark: MarkStepHandler;
}) {
  const [asking, setAsking] = useState<'right' | 'wrong' | null>(null);
  const [misread, setMisread] = useState(false);
  const [text, setText] = useState(item.marking?.text ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = item.marking?.verdict;

  const close = () => { setAsking(null); setMisread(false); };
  const save = async (verdict: 'right' | 'wrong' | 'clear', why?: 'misread' | 'misjudged', said?: string) => {
    setBusy(true); setError(null);
    try {
      await onMark(step, verdict, why, said?.trim() || undefined);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the correction');
    } finally {
      setBusy(false);
    }
  };
  const tap = (verdict: 'right' | 'wrong') => {
    if (verdict === current) return save('clear');
    // Against a definite analyser verdict: ask why, optionally.
    if ((system === 'right' || system === 'wrong') && verdict !== system) { setAsking(verdict); return; }
    // Changing the verdict keeps a misread already said.
    return item.marking?.why === 'misread' ? save(verdict, 'misread', item.marking.text) : save(verdict);
  };
  const verdictBtn = (verdict: 'right' | 'wrong', onClick: () => void, pressed: boolean) => (
    <button type="button" className={`sa-mark-btn ${verdict}${pressed ? ' on' : ''}`} aria-pressed={pressed} disabled={busy}
      data-user-action={`step_mark_${verdict}`}
      title={pressed ? 'Clear your correction' : verdict === 'right' ? 'The student’s step is right' : 'The student’s step is wrong'}
      onClick={onClick}>{verdict === 'right' ? '✓ Right' : '✕ Wrong'}</button>
  );

  if (misread) {
    // What is written, and the student's step judged on that.
    const picked = asking ?? current;
    return (
      <div className="sa-mark">
        <span className="sa-mark-ask">
          <span className="sa-mark-q">We misread it. It says:</span>
          <input type="text" className="sa-mark-input" placeholder="what is written (optional)" value={text} maxLength={500}
            onChange={e => setText(e.target.value)} autoFocus />
        </span>
        <span className="sa-mark-ask">
          <span className="sa-mark-q">Student’s step:</span>
          {verdictBtn('right', () => save('right', 'misread', text), picked === 'right')}
          {verdictBtn('wrong', () => save('wrong', 'misread', text), picked === 'wrong')}
          <button type="button" className="sa-mark-skip" disabled={busy} onClick={close}>Cancel</button>
        </span>
        {error && <span className="sa-mark-err" role="alert">{error}</span>}
      </div>
    );
  }

  return (
    <div className="sa-mark">
      <span className="sa-mark-btns" role="group" aria-label={`Student’s step ${item.stepNumber}`}>
        <span className="sa-mark-q">Student’s step:</span>
        {verdictBtn('right', () => tap('right'), current === 'right')}
        {verdictBtn('wrong', () => tap('wrong'), current === 'wrong')}
      </span>
      {!asking && (
        <button type="button" className="sa-mark-skip" disabled={busy} data-user-action="step_mark_misread" onClick={() => setMisread(true)}>
          {item.marking?.why === 'misread' ? 'Edit what it says' : 'Misread?'}
        </button>
      )}
      {asking && (
        <span className="sa-mark-ask">
          <span className="sa-mark-q">Why did we get it wrong?</span>
          <button type="button" className="sa-mark-chip" disabled={busy} onClick={() => setMisread(true)}>We misread it</button>
          <button type="button" className="sa-mark-chip" disabled={busy} onClick={() => save(asking, 'misjudged')}>We judged it wrong</button>
          <button type="button" className="sa-mark-skip" disabled={busy} onClick={() => save(asking)}>Skip</button>
        </span>
      )}
      {error && <span className="sa-mark-err" role="alert">{error}</span>}
    </div>
  );
}
